/**
 * O'zbek lotin → kirill transliteratsiyasi.
 * Lab matnlarida 'uz-Cyrl' yetishmasa avtomatik hosil qilinadi. Qoidalar 1995 alifbosi
 * bo'yicha; istisnolar `OVERRIDES` da. Natija tahrirlanishi kerak bo'lgan joylar bo'lishi
 * mumkin (masalan "ts" → "ц" faqat o'zlashgan so'zlarda) — bu yordamchi, tarjimon emas.
 */

/**
 * O'zak istisnolari: so'z shu o'zak bilan boshlansa, o'zak lug'atdan, qo'shimcha qoida bilan
 * (reaksiya → реакция, reaksiyalari → реакциялари). Uzun o'zaklar avval tekshiriladi.
 */
const OVERRIDES: Record<string, string> = {
  sirkulyatsiya: 'циркуляция',
  konsentratsiya: 'концентрация',
  rektifikatsiya: 'ректификация',
  filtratsiya: 'фильтрация',
  filtr: 'фильтр',
  absorbsiya: 'абсорбция',
  adsorbsiya: 'адсорбция',
  reaksiya: 'реакция',
  funksiya: 'функция',
  operatsiya: 'операция',
  proporsional: 'пропорционал',
  koeffitsient: 'коэффициент',
  potensial: 'потенциал',
  seld: 'цельд',
  fakultet: 'факультет',
  rol: 'роль',
  ventil: 'вентиль',
  sulfat: 'сульфат',
  sulfit: 'сульфит',
  dizel: 'дизель',
  retsikl: 'рецикл',
  purge: 'пурж',
  altshul: 'альтшуль',
  shatele: 'шателье',
  psevdo: 'псевдо',
  dvigatel: 'двигатель',
  model: 'модель',
  modul: 'модуль',
  sentrifuga: 'центрифуга',
  sentrifugalash: 'центрифугалаш',
  sement: 'цемент',
  protsess: 'процесс',
  litsenziya: 'лицензия',
  kompyuter: 'компьютер',
  server: 'сервер',
  internet: 'интернет',
  kabel: 'кабель',
  nikel: 'никель',
  spirt: 'спирт',
  stirol: 'стирол',
  etanol: 'этанол',
  etan: 'этан',
  etilen: 'этилен',
  eksperiment: 'эксперимент',
  ekspert: 'эксперт',
  element: 'элемент',
};
const OVERRIDE_KEYS = Object.keys(OVERRIDES).sort((a, b) => b.length - a.length);

/**
 * Lotincha qoldiriladigan so'zlar: xorijiy familiyalar, texnik atamalar va qisqartmalar
 * (kirill matnida ham lotincha yoziladi). Tekshiruv registrga sezgir emas.
 */
const KEEP = new Set(
  [
    'JSON', 'PDF', 'CSV', 'API', 'CLI', 'SVG', 'URL', 'HTML', 'CSS', 'PWA', 'ID', 'UI',
    'LMTD', 'NTU', 'CSTR', 'PFR', 'NPSH', 'BPE', 'HTU', 'NOG', 'RK4', 'MPa', 'kPa', 'kW', 'MW',
    'McCabe', 'Thiele', 'Kremser', 'Colburn', 'Arrhenius', 'Ruth', 'Darcy', 'Weisbach', 'Fenske',
    'Underwood', 'Souders', 'Bowman', 'Colebrook', 'Kirchhoff', 'Wegstein', 'Tarjan', 'Ambler',
    'Stokes', 'Antoine', 'Raoult', 'Hoff', 'van', 'Gibbs', 'Newton', 'Reynolds', 'Nusselt', 'Prandtl',
    'Temkin', 'Pyzhev', 'Graaf', 'Eklund', 'Hamielec', 'Hui', 'Gillespie', 'Beattie', 'Shomate',
    'Vogel', 'Sutherland', 'Watson', 'Perry', 'NIST', 'Laravel', 'Vue', 'Vite', 'Node', 'TypeScript',
    'Web', 'Worker', 'Multi', 'tenant', 'domain', 'pack', 'engine', 'Engine', 'MusLab', 'Sanctum',
    'Postgres', 'Redis', 'Pinia', 'zod', 'Zod', 'NOBOOK', 'Seed', 'seed', 'Kp', 'Kc', 'pH',
    'runaway', 'Brown', 'Churchill', 'Plug', 'flow', 'const', 'UA', 'Re', 'Nu', 'Pr',
    'kg', 'mol', 'kmol', 'Pa', 'bar', 'kJ', 'mm', 'cm', 'km', 'min', 'rpm',
    // kimyoviy formulalar va katalizatorlar
    'CO', 'NO', 'NOx', 'HC', 'Cu', 'ZnO', 'Pt', 'Rh', 'Fe', 'Zn', 'Mg', 'Ca', 'Na', 'Cl',
    'flowsheet', 'provisional', 'lump', 'feed', 'exp', 'ln', 'log', 'Moody', 'vant',
  ].map((w) => w.toLowerCase()),
);

const DIGRAPHS: [RegExp, string][] = [
  [/o[ʻ'’`]/g, 'ў'],
  [/O[ʻ'’`]/g, 'Ў'],
  [/g[ʻ'’`]/g, 'ғ'],
  [/G[ʻ'’`]/g, 'Ғ'],
  // o'zlashgan so'zlardagi -tsiya / -ksiya / -bsiya → -ция / -кция / -бция
  [/tsiya/g, 'ция'],
  [/Tsiya/g, 'Ция'],
  [/TSIYA/g, 'ЦИЯ'],
  [/ksiya/g, 'кция'],
  [/KSIYA/g, 'КЦИЯ'],
  [/bsiya/g, 'бция'],
  [/BSIYA/g, 'БЦИЯ'],
  [/sh/g, 'ш'],
  [/Sh|SH/g, 'Ш'],
  [/ch/g, 'ч'],
  [/Ch|CH/g, 'Ч'],
  [/yo/g, 'ё'],
  [/Yo|YO/g, 'Ё'],
  [/yu/g, 'ю'],
  [/Yu|YU/g, 'Ю'],
  [/ya/g, 'я'],
  [/Ya|YA/g, 'Я'],
  [/ye/g, 'е'],
  [/Ye|YE/g, 'Е'],
  [/ng/g, 'нг'],
];

const SINGLE: Record<string, string> = {
  a: 'а',
  b: 'б',
  d: 'д',
  e: 'е',
  f: 'ф',
  g: 'г',
  h: 'ҳ',
  i: 'и',
  j: 'ж',
  k: 'к',
  l: 'л',
  m: 'м',
  n: 'н',
  o: 'о',
  p: 'п',
  q: 'қ',
  r: 'р',
  s: 'с',
  t: 'т',
  u: 'у',
  v: 'в',
  x: 'х',
  y: 'й',
  z: 'з',
  c: 'ц',
  w: 'в',
  A: 'А',
  B: 'Б',
  D: 'Д',
  E: 'Е',
  F: 'Ф',
  G: 'Г',
  H: 'Ҳ',
  I: 'И',
  J: 'Ж',
  K: 'К',
  L: 'Л',
  M: 'М',
  N: 'Н',
  O: 'О',
  P: 'П',
  Q: 'Қ',
  R: 'Р',
  S: 'С',
  T: 'Т',
  U: 'У',
  V: 'В',
  X: 'Х',
  Y: 'Й',
  Z: 'З',
  C: 'Ц',
  W: 'В',
};

function translitWord(word: string): string {
  const lower = word.toLowerCase();
  if (KEEP.has(lower) || KEEP.has(lower.replace(/[ʻ'’`]/g, ''))) return word;
  // Unlisiz bosh harfli qisqartma (RT, NPSH, PFR, CSTR) — formula yoki texnik atama; o'zbekcha so'zda unli bor
  if (word.length >= 2 && word.length <= 5 && /^[A-Z]+$/.test(word) && !/[AEIOU]/.test(word)) return word;
  // Bosh harflardan iborat qisqartma (2+ harf, unlisiz yoki lotin atama) — ko'pincha texnik
  const capital = word[0] !== lower[0];
  const stem = OVERRIDE_KEYS.find((k) => lower.startsWith(k));
  if (stem) {
    const ov = OVERRIDES[stem]!;
    const head = capital ? ov[0]!.toUpperCase() + ov.slice(1) : ov;
    const rest = word.slice(stem.length);
    // qo'shimcha oldidan yumshatish belgisi tushadi (модель → моделлар)
    return rest ? head.replace(/ь$/, '') + translitPlain(rest) : head;
  }
  return translitPlain(word);
}

function translitPlain(word: string): string {
  let w = word;
  // So'z boshidagi "e" → "э"
  w = w.replace(/^e/, 'э').replace(/^E/, 'Э');
  for (const [re, to] of DIGRAPHS) w = w.replace(re, to);
  // Tutuq belgisi (unli emas harfdan keyin) → ъ
  w = w.replace(/[ʻ'’`]/g, 'ъ');
  let out = '';
  for (const ch of w) out += SINGLE[ch] ?? ch;
  return out;
}

/** Faqat lotin harflaridan iborat so'zlar transliteratsiya qilinadi; raqamlar, formulalar, ` ` ichidagi kod tegilmaydi. */
export function uzLatnToCyrl(text: string): string {
  // Inline kod, formulalar (backtick, $...$), {placeholder}lar, fayl yo'llari (a/b/c, x.json) saqlanadi
  const parts = text.split(/(`[^`]*`|\$[^$]*\$|\{[^}]*\}|[\w.-]*\/[\w.-]*\/[\w./-]+|[\w-]+\.[a-z]{2,4}\b)/g);
  return parts
    .map((part, i) => {
      if (i % 2 === 1) return part;
      // So'z boshi: oldida harf, raqam, `_`, indeks yoki ° bo'lmasin; oxirida `_`, indeks yoki raqam bo'lmasin
      return part.replace(
        /(?<![A-Za-z0-9_₀-₉⁰-⁹°α-ωΑ-Ω∆Δ])[A-Za-z][A-Za-zʻ'’`]*(?![A-Za-z0-9_₀-₉⁰-⁹α-ωΑ-Ω])/g,
        (w) => (w.length === 1 && !/^[uU]$/.test(w) ? w : translitWord(w)),
      );
    })
    .join('');
}
